import type {TimeEntry} from '@package/shared'
import {useCallback, useState} from 'react'
import dayjs from 'dayjs'
import {TimeEntryContainer} from './TimeEntryContainer.tsx'
import {EditableCard} from '../../components/editableCard/EditableCard.tsx'
import {useLocalSettings} from '../../providers/localSettings/useLocalSettings.ts'
import type {DateRange} from '../../components/dateRangeSelector/DateRangeSelector.tsx'
import {UpdateTimeEntryForm} from './forms/timeEntry/UpdateTimeEntryForm.tsx'
import {useDayItems} from '../../providers/dayItems/useDayItems.ts'

type Props = {
  day: DateRange
  entry: TimeEntry
  editMode: boolean
  onEditModeEntered: (entry: TimeEntry) => void
  onEditModeLeft: (entry: TimeEntry) => void
  disabled: boolean
}

export const TimeEntryEditCard = ({
  day,
  entry,
  editMode,
  onEditModeEntered,
  onEditModeLeft,
  disabled: containerDisabled,
}: Props) => {
  const {updateTimeEntry, deleteTimeEntry} = useDayItems()
  const [updating, setUpdating] = useState<boolean>(false)
  const [deleting, setDeleting] = useState<boolean>(false)
  const settings = useLocalSettings()
  const [modifiedEntry, setModifiedEntry] = useState<TimeEntry>({...entry})
  const [previousEntry, setPreviousEntry] = useState<TimeEntry>(entry)
  if (entry !== previousEntry) {
    setPreviousEntry(entry)
    setModifiedEntry({...entry})
  }

  const handleEditModeEntered = useCallback(() => {
    setModifiedEntry({...entry})
    onEditModeEntered(entry)
  }, [entry, onEditModeEntered])

  const handleCancel = useCallback(() => {
    onEditModeLeft(entry)
  }, [onEditModeLeft, entry])

  const disabled = containerDisabled

  return (
    <EditableCard
      p={'xs'}
      editMode={editMode}
      onEditModeEntered={handleEditModeEntered}
      onEditModeLeft={handleCancel}
      disabled={disabled}
    >
      {!editMode && (
        <TimeEntryContainer
          start={dayjs(entry.start)}
          end={dayjs(entry.end)}
          description={entry.description}
          breakHours={entry.breakHours}
          sourceType={null}
          projectId={entry.projectId}
          merged={false}
          compact={settings.compactMode}
        />
      )}

      {editMode && (
        <UpdateTimeEntryForm
          onUpdate={async (request) => {
            setUpdating(true)
            const timeEntry = await updateTimeEntry(request).finally(() => setUpdating(false))
            onEditModeLeft(timeEntry)
          }}
          updating={updating}
          onDelete={async (timeEntry) => {
            setDeleting(true)
            await deleteTimeEntry(timeEntry).finally(() => setDeleting(false))
            onEditModeLeft(timeEntry)
          }}
          deleting={deleting}
          day={day}
          entry={modifiedEntry}
          disabled={disabled}
        />
      )}
    </EditableCard>
  )
}
